import axios from "axios";


// Determine API base URL based on environment
export const getApiBaseUrl = () => {
  // In production (Docker), API is proxied through nginx at /api
  // In development, use environment variable or localhost

  if (process.env.NODE_ENV === "production") {
    // Use relative URL - nginx will proxy to backend
    return "/api";
  }

  // Development: use environment variable or default to localhost
  return process.env.REACT_APP_API_URL || "http://localhost:5000/api";
};

const API_URL = getApiBaseUrl();


// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

console.log("🔧 API Service - Created adminAPI instance");

// Add request interceptor to attach auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    console.log("Inserting token into request", token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem("token");
      window.location.href = "/portal/login";
    }
    return Promise.reject(error);
  }
);

// Member/Family API calls
export const memberAPI = {
  // Get family members
  getFamily: async () => {
    const response = await api.get("/lig/family");
    return response.data;
  },

  // Create new family
  createFamily: async (userName) => {
    const response = await api.post("/lig/family", { userName });
    return response.data;
  },

  // Update member
  updateMember: async (member) => {
    const response = await api.put("/lig/members", { member });
    return response.data;
  },

  // Delete member
  deleteMember: async (uname) => {
    const response = await api.delete("/lig/members", { data: { uname } });
    return response.data;
  },

  // Delete temporary member
  deleteTempMember: async (id) => {
    const response = await api.delete("/lig/members/temp", { data: { id } });
    return response.data;
  },

  // Invite member
  invite: async (email, name, nickName) => {
   const response = await  api.post("/lig/members/invite", { email, name, nickName });
   return response.data;
  },

  // Accept invitation
  welcome: async (tempID) => {
    const response = await api.post(`/lig/members/welcome/${tempID}`);
    return response.data;
  },

  // Get member stats
  getStats: async () => {
    const response = await api.get("/lig/members/stats");
    return response.data;
  },
};

// Notes API calls
export const notesAPI = {
  // Get user notes
  getNotes: async () => {
    const response = await api.get("/lig/notes");
    console.log("loaded notes", response.data);
    return response.data;
  },

  // Get more notes (pagination)
  getMoreNotes: async (lastDate) => {
    const response = await api.get(`/lig/notes/more/${lastDate}`);
    return response.data;
  },

  // Get family notes for a year
  getNotesFamily: async (year) => {
    const response = await api.get(`/lig/notes/family/${year}`);
    return response.data;
  },

  // Get more family notes (pagination)
  getMoreNotesFamily: async (lastDate) => {
    const response = await api.get(`/lig/notes/family/more/${lastDate}`);
    return response.data;
  },

  // Create note
  addNote: async (note) => {
    const response = await api.post("/lig/notes", { note });
    console.log("addNote response.data", response.data);
    return response.data;
  },

  // Update note
  updateNote: async (note) => {
    const response = await api.put("/lig/notes", { note });
    return response.data;
  },

  // Delete note
  deleteNote: async (noteID, uname, date) => {
    console.log("about to deleteNote", noteID, uname, date);
    const response = await api.delete("/lig/notes", {
      data: { noteID, uname, date },
    });
    console.log("deleteNote response.data", response.data);
    return response.data;
  },

  uploadPhoto: async (noteId, imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("noteId", noteId);

    const response = await api.post("/images/lig", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  removePhoto: async (noteId) => {
    const response = await api.delete("/images/lig", {
      data: { noteId },
    });
    return response.data;
  },
};

// Years API calls
export const yearsAPI = {
  // Get years with notes
  getYears: async (excludeLast = false) => {
    const response = await api.get(`/lig/years/${excludeLast}`);
    return response.data;
  },
};

// ligfrontend/src/services/api.js

export const adminAPI = {
  // Job management
  getJobStatus: async () => {
    const response = await api.get("/ligadmin/jobs/status");
    return response.data;
  },

  triggerJob: async (jobName) => {
    const response = await api.post(`/ligadmin/jobs/${jobName}/trigger`);
    return response.data;
  },

  // Email previews
  previewNudgeEmail: async (data) => {
    const response = await api.post("/email/preview/nudge", data);
    return response.data;
  },

  previewInvitationEmail: async (data) => {
    const response = await api.post("/email/preview/invitation", data);
    return response.data;
  },

  getMembersList: async () => { 
    const response = await api.get("/email/members-list");
    return response.data;
  },

  // Email testing
  testEmailConnection: async () => {
    const response = await api.get("/email/test-connection");
    return response.data;
  },

  sendTestEmail: async (data) => {
    const response = await api.post("/email/send-test", data);
    return response.data;
  },
};

export default api;
